import React from 'react'
import get from 'lodash/get'
import type { SelectInlineInputProps } from '@ztechapps/webui.input'
import { SelectInlineInput } from '@ztechapps/webui.input'
import { UnitOfMeasureSelect } from '../select/UnitOfMeasureSelect'
import { useUnitOfMeasureSelectOptions } from '../select/useUnitOfMeasureSelectOptions'

export type BaseUomSelectInlineInputProps = Partial<SelectInlineInputProps> & {
  category?: string
}

/**
 * Custom inline input for base/inventory UOM that:
 * When changed, also updates orderUom, purchaseUom, workOrderUom to the same value
 * and sets all conversion factors to 1
 */
export const BaseUomSelectInlineInput = ({ category, name, initialValues, ...rest }: BaseUomSelectInlineInputProps) => {
  const options = useUnitOfMeasureSelectOptions({ category })

  // RelatedEntity: flatten resolved object to _id string for matching
  const resolvedObject = get(initialValues, name)
  const flattenedId = resolvedObject?._id ?? resolvedObject
  const flattenedInitialValues = {
    ...initialValues,
    [name]: flattenedId,
  }

  return (
    <SelectInlineInput<BaseUomSelectInlineInputProps>
      {...rest}
      name={name}
      initialValues={flattenedInitialValues}
      selectComponent={UnitOfMeasureSelect}
      selectOptions={options}
      inputProps={{ category }}
      displayContent={() => {
        if (!resolvedObject) return null
        return `${resolvedObject.name} (${resolvedObject.symbol})`
      }}
      updateParams={({ updatedValue, _id }) => {
        const uomRef = updatedValue?.value ? { _id: updatedValue.value } : null

        return {
          input: {
            _id,
            // Update base UOM
            [name]: uomRef,
            // Reset all context UOMs to the same value as base
            orderUom: uomRef,
            purchaseUom: uomRef,
            workOrderUom: uomRef,
            // Set all conversion factors to 1
            orderConversionFactor: 1,
            purchaseConversionFactor: 1,
            workOrderConversionFactor: 1,
          },
        }
      }}
    />
  )
}
